import {
  capitalize,
  convert24HourTo12Hour,
  DeliveryAssignmentEntity,
  DeliveryAssignmentNonOperatingConditionsEntity,
  DeliveryAssignmentProviderEntity,
  DeliveryAssignmentTimeEntity,
  displayCentsInDollars,
  renderProviderName,
  renderVDriveProviderName,
  VDriveStrategySection,
  VDriveStrategySectionKey,
  VDriveStrategySuggestion,
} from '@vgrubs/components';

const join = (parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' · ');

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/** "10:00 AM - 9:00 PM", or nothing when the rule has no window. */
const timeWindow = (start?: string | null, end?: string | null) =>
  start && end
    ? `${convert24HourTo12Hour(start)} - ${convert24HourTo12Hour(end)}`
    : null;

const miles = (distance?: number | null) =>
  distance ? `under ${distance} mi` : null;

/** "MotoClick · under 15 mi · 10:00 AM - 9:00 PM · fallback DoorDash". */
const providerRuleLine = (rule: DeliveryAssignmentProviderEntity) =>
  join([
    renderVDriveProviderName(rule.provider),
    miles(rule.distanceMiles),
    timeWindow(rule.startTime, rule.endTime),
    !!rule.orderProviders?.length &&
      `${rule.orderProviders
        .map((p) => renderProviderName(p))
        .join(', ')} orders`,
    !!rule.fallbackProviders?.length &&
      `fallback ${rule.fallbackProviders
        .map(renderVDriveProviderName)
        .join(', ')}`,
  ]);

/** "Under $25.00 · 11:00 AM - 2:00 PM · 15 min prep · Relay". */
const timeRuleLine = (time: DeliveryAssignmentTimeEntity) =>
  join([
    time.amount != null && `Under ${displayCentsInDollars(time.amount)}`,
    timeWindow(time.startTime, time.endTime),
    time.minutes != null &&
      (time.minutes === 0 ? 'ASAP' : `${time.minutes} min prep`),
    time.provider && renderVDriveProviderName(time.provider),
  ]);

/** "Monday · under 5 mi · 10:00 AM - 2:00 PM · Grubhub orders". */
const nonOperatingLine = (
  condition: DeliveryAssignmentNonOperatingConditionsEntity
) =>
  join([
    capitalize(condition.day || ''),
    miles(condition.distanceMiles),
    timeWindow(condition.startTime, condition.endTime),
    condition.orderProvider &&
      `${renderProviderName(condition.orderProvider)} orders`,
  ]);

const listOr = (lines: string[], empty: string) =>
  lines.length ? lines : [empty];

/**
 * One section's value as lines, in the words the strategy screen's cards
 * use, so "now" and "suggested" can be read side by side.
 */
export const sectionValueLines = (
  key: VDriveStrategySectionKey,
  value: Partial<DeliveryAssignmentEntity>
): string[] => {
  switch (key) {
    case 'providers':
      return listOr((value.providers ?? []).map(providerRuleLine), 'No rules');
    case 'times':
      return listOr((value.times ?? []).map(timeRuleLine), 'No times');
    case 'deliveryNowReassign':
      return [
        value.deliveryNowReassignEnabled
          ? `On, after ${value.deliveryNowReassignMinutes ?? '-'} min`
          : 'Off',
      ];
    case 'bannedProviders':
      return [
        value.bannedProviders?.length
          ? value.bannedProviders.map(renderVDriveProviderName).join(', ')
          : 'None',
      ];
    case 'preferredAssignmentType':
      return [capitalize(value.preferredAssignmentType ?? '-')];
    case 'nonOperatingConditions':
      return listOr(
        (value.nonOperatingConditions ?? []).map(nonOperatingLine),
        'No conditions'
      );
    case 'ignoreGlobalStrategies':
      return [value.ignoreGlobalStrategies ? 'On' : 'Off'];
  }
};

export type EstimateTone = 'good' | 'bad' | 'neutral';

/** Negative is better for all three: fewer late, faster, cheaper. */
const toneOf = (change: number): EstimateTone =>
  change < 0 ? 'good' : change > 0 ? 'bad' : 'neutral';

/* A plain hyphen for minus, as everywhere in the app. */
const signed = (change: number, text: string) =>
  `${change > 0 ? '+' : change < 0 ? '-' : ''}${text}`;

const round1 = (value: number) => Number(value.toFixed(1));

/** The AI's estimates for one section as badges; null estimates are left out. */
export const sectionEstimates = (section: VDriveStrategySection) => {
  const estimates: { label: string; tone: EstimateTone }[] = [];
  const late = section.estimatedLateRateChange;
  const minutes = section.estimatedMedianMinutesChange;
  const cost = section.estimatedCostChangePerDeliveryCents;
  if (late != null)
    estimates.push({
      label: signed(late, `${Math.abs(round1(late))} pp late`),
      tone: toneOf(late),
    });
  if (minutes != null)
    estimates.push({
      label: signed(minutes, `${Math.abs(round1(minutes))} min`),
      tone: toneOf(minutes),
    });
  if (cost != null)
    estimates.push({
      label: signed(
        cost,
        `${displayCentsInDollars(Math.abs(cost))} / delivery`
      ),
      tone: toneOf(cost),
    });
  return estimates;
};

const percent = (value: number | null) =>
  value == null ? '-' : `${round1(value)}%`;

const minutesLabel = (value: number | null) =>
  value == null ? '-' : `${Math.round(value)} min`;

/** The four numbers in the panel header, with what each one is measured on. */
export const baselineFigures = ({ baseline }: VDriveStrategySuggestion) => [
  {
    label: 'Late (>20 min)',
    value: percent(baseline.lateRate),
    hint:
      baseline.lateRate == null
        ? 'No promised times'
        : `of the ${percent(baseline.promiseCoverage)} with a promised time`,
  },
  {
    label: 'Median delivery',
    value: minutesLabel(baseline.medianTotalMinutes),
    hint:
      baseline.p90TotalMinutes == null
        ? null
        : `90% within ${minutesLabel(baseline.p90TotalMinutes)}`,
  },
  {
    label: 'Avg fleet price',
    value:
      baseline.avgProviderFeeCents == null
        ? '-'
        : displayCentsInDollars(baseline.avgProviderFeeCents),
    hint: null,
  },
  {
    label: 'Needing reassign',
    value: percent(baseline.reassignShare),
    hint: 'orders with more than one attempt',
  },
];

/** "Last 90 days · 70 deliveries · 0.8 a day". */
export const dataLine = ({ data, baseline }: VDriveStrategySuggestion) =>
  join([
    'Last 90 days',
    plural(data.completed, 'delivery', 'deliveries'),
    `${round1(baseline.completedPerDay)} a day`,
  ]);

export const confidenceLabel = (
  confidence: VDriveStrategySection['confidence']
) => `${capitalize(confidence)} confidence`;

/** Sections the admin may tick: a change that passed validation. */
export const isApplicable = (section: VDriveStrategySection) =>
  section.changed && section.applicable;

/**
 * The form fields the ticked sections set. Each section carries only its own
 * fields, so the order does not matter and every field left out (anything the
 * admin typed, or no section touched) stays as it is. Only these are written,
 * never the whole form, which is what lets them land on a form that is still
 * being loaded in the same render (arriving from the dashboard).
 */
export const suggestedFields = (
  sections: VDriveStrategySection[]
): Partial<DeliveryAssignmentEntity> =>
  sections
    .filter(isApplicable)
    .reduce((fields, section) => ({ ...fields, ...section.suggested }), {});

/**
 * Route state for opening a strategy screen with the AI's changes filled in:
 * the dashboard's Worst Performing Locations list sends the keys the admin
 * ticked there, the screen applies them from the same (cached) job once.
 */
export type StrategyRouteState = {
  /** The brand's name; the location's comes with its vDrive. */
  name?: string;
  aiSections?: VDriveStrategySectionKey[];
} | null;
